import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import MomentCore from "moment";

import { makeStyles, useTheme, useMediaQuery, Toolbar, Grid } from "@material-ui/core";

import Fido from "../utils/Fido";
import FilterSelect from "../components/FilterSelect";
import Constants from "../utils/Constants";
import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import MetricsHeaderBarPanel from "../components/MetricsHeaderBarPanel";

const styles = makeStyles(theme => ({
	toolbar: {
		backgroundColor: theme.palette.primary.cyan,
		"& *": {
			color: theme.palette.grey[200]
		}
	},

	pipe: {
		width: 1,
		content: '""',
		display: "inline-block",
		height: theme.spacing(5),
		backgroundColor: theme.palette.grey["A200"]
	},

	firstPanel: {
		paddingLeft: 0,
		paddingRight: theme.spacing(7),
		'& .title': {
			fontSize: 18
		}
	},

	daySelector: {
		"& button": {
			padding: 0,
			maxHeight: theme.spacing(2.125)
		}
	},

	panel: {
		color: "white"
	}
}));

const defaultMetricsData = {
	totalPosts: null,
	totalImages: null,
	totalDiscussions: null,
	totalAlerts: null
};

const defaultShareitData = {
	totalTips: null
};

function MetricsHeaderBar(props) {
	const authenticatedUser = useContext(AuthorizedUserContext);

	const [previousDays, setPreviousDays] = useState(30);
	const [metricsData, setMetricsData] = useState(defaultMetricsData);
	const [shareitMetricsData, setShareitMetricsData] = useState(defaultShareitData);

	let classes = styles(),
		theme = useTheme(),
		{className, showSnackbar} = props,
		isMD = useMediaQuery(theme.breakpoints.up("md")),
		hasShareit = AuthorizedUser.hasShareit(authenticatedUser);

	const getDates = (previousDays) => {
		let format = "YYYY-MM-DD";
		return {
			sDate: ((new MomentCore()).subtract(previousDays-1, "days")).format(format),
			eDate: (new MomentCore()).format(format)
		};
	};

	// load the data
	useEffect(() => {
		let fido = new Fido(),
			shareitFido = new Fido();

		(async function loadData() {
			let data = await fido.fetch("/reports/overview/spark", {
				query: {...getDates(previousDays)}})
				.catch(error => showSnackbar(error.message));

			if (data) {
				setMetricsData(data);
			}
		})();

		if (hasShareit) {
			(async function loadShareitData() {
				let data = await shareitFido.fetch("/reports/tips/spark", {
					query: {...getDates(previousDays)}})
					.catch(error => showSnackbar(error.message));

				if (data) {
					setShareitMetricsData(data);
				}
			})();
		}

		return () => {
			fido.dropIt();
			shareitFido.dropIt();
		};
	}, [previousDays, showSnackbar, hasShareit]);

	const changeDays = (selected) => {
		// otherwise when filter selects being setup it goes into infinite loop
		if (selected.previousDays[0] !== previousDays) {
			setMetricsData(defaultMetricsData);
			setShareitMetricsData(defaultShareitData);
			setPreviousDays(selected.previousDays);
		}
	};

	return (
		<Toolbar className={`${classes.toolbar} ${className}`}>
			{AuthorizedUser.hasReports(authenticatedUser) ?
				<React.Fragment>
					<Grid container alignItems='center'>
						<MetricsHeaderBarPanel
							excludeChevron
							title="Metrics"
							selector={
								<FilterSelect
									singleSelect
									name="previousDays"
									options={Constants.REPORT.DAYS}
									defaultSelections={[previousDays]}
									menuTitle={`${previousDays} Days`}
									handleUpdate={changeDays}
									className={classes.daySelector}
									ariaLabel="Metrics previous days selector" />
							}
							className={classes.firstPanel} />
						<MetricsHeaderBarPanel
							title="Associations"
							className={classes.panel}
							value={metricsData.totalPosts}
							change={metricsData.postsAmountChange} />
						
						<MetricsHeaderBarPanel
							title={`Images${isMD ? " Scanned" : ""}`}
							value={metricsData.totalImages}
							change={metricsData.imagesAmountChange} />
						
						<MetricsHeaderBarPanel
							invertChevron
							title="Discussions"
							value={metricsData.totalDiscussions}
							change={metricsData.discussionsAmountChange} />

						<MetricsHeaderBarPanel
							invertChevron
							title="Alerts"
							value={metricsData.totalAlerts}
							change={metricsData.alertsAmountChange} />

						{hasShareit ? (
							<React.Fragment>
								<MetricsHeaderBarPanel
									title="Tips"
									value={shareitMetricsData.totalTips} />
							</React.Fragment>
						) : null}
					</Grid>
				</React.Fragment> : null
			}
		</Toolbar>
	);
}

MetricsHeaderBar.propTypes = {
	className: PropTypes.string,
	showSnackbar: PropTypes.func.isRequired
};

//MetricsHeaderBar.whyDidYouRender = Constants.ENV.IS_LOCAL;

export default MetricsHeaderBar;